import React, { useState, useEffect, useCallback, useRef } from 'react';
import authService, { OnlineUser, ActivityEvent } from '../services/auth.service';

const ONLINE_REFRESH_MS = 15000;

// Libellés lisibles par identifiant d'app
const APP_LABELS: { [key: string]: string } = {
  dashboard: 'Dashboard',
  rh: 'Tâches',
  planning: 'Planning',
  programming: 'Programmation',
  playlist: 'Playlist',
  filemanager: 'File Manager',
  conducteur: 'Conducteur',
  magasin: 'Magasin',
  vault: 'Vault',
  live: 'Live',
  difizyon: 'Difizyon',
  alerting: 'Alerting',
  videowall: "Mur d'image",
  rustdesk: 'RustDesk',
  bescor: 'Bescor PTZ',
  portal: 'Portail',
  volumes: 'Volumes',
  network: 'Network',
  agent: 'Agent',
  lexique: 'Lexique',
  'regie-pub': 'Régie Pub',
  regie_pub: 'Régie Pub',
  achats: 'Achats',
  captations: 'Captations',
};

const ACTION_LABELS: { [key: string]: { label: string; color: string; bg: string } } = {
  login: { label: 'Connexion', color: '#4ade80', bg: 'rgba(34,197,94,0.15)' },
  enter: { label: 'Ouverture', color: '#60a5fa', bg: 'rgba(59,130,246,0.15)' },
  logout: { label: 'Déconnexion', color: '#f87171', bg: 'rgba(239,68,68,0.15)' },
};

const appLabel = (id: string) => APP_LABELS[id] || id;

const ROLE_COLORS: { [key: string]: string } = {
  admin: '#f59e0b',
  user: '#60a5fa',
  prestataire: '#a78bfa',
  guest: '#94a3b8',
};

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diff = Math.max(0, Date.now() - then);
  const s = Math.floor(diff / 1000);
  if (s < 60) return `il y a ${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `il y a ${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `il y a ${h} h`;
  const d = Math.floor(h / 24);
  return `il y a ${d} j`;
}

function formatDateTime(iso: string): string {
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return iso;
  return dt.toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

const RoleBadge: React.FC<{ role: string }> = ({ role }) => {
  const r = (role || '').toLowerCase();
  const color = ROLE_COLORS[r] || '#94a3b8';
  return (
    <span
      style={{
        fontSize: '0.65rem',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        color,
        border: `1px solid ${color}55`,
        background: `${color}1a`,
        borderRadius: 6,
        padding: '1px 7px',
      }}
    >
      {r || '—'}
    </span>
  );
};

const AppBadge: React.FC<{ app: string }> = ({ app }) => (
  <span
    style={{
      fontSize: '0.75rem',
      fontWeight: 600,
      color: '#cbd5e1',
      background: '#181b1f',
      border: '1px solid #353a40',
      borderRadius: 6,
      padding: '2px 8px',
    }}
  >
    {appLabel(app)}
  </span>
);

const ActivityMonitor: React.FC = () => {
  const [online, setOnline] = useState<OnlineUser[]>([]);
  const [events, setEvents] = useState<ActivityEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);

  const [appFilter, setAppFilter] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [limit, setLimit] = useState(100);
  // Masque les activités des comptes admin (activé par défaut)
  const [hideAdmin, setHideAdmin] = useState(true);

  // Liste stable des utilisateurs connus (pour le filtre), enrichie au fil des chargements
  const [knownUsers, setKnownUsers] = useState<{ id: number; name: string }[]>([]);

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchOnline = useCallback(async (silent = false) => {
    if (!silent) setRefreshing(true);
    try {
      const res = await authService.getOnlineUsers();
      if (res.success && res.online) {
        setOnline(res.online);
        setLastRefresh(new Date());
        setError(null);
      } else if (!res.success) {
        setError(res.message || 'Erreur de chargement');
      }
    } catch (e: any) {
      setError("Impossible de récupérer les utilisateurs en ligne");
    } finally {
      if (!silent) setRefreshing(false);
    }
  }, []);

  const fetchHistory = useCallback(async () => {
    try {
      const res = await authService.getActivityHistory({
        limit,
        app: appFilter || undefined,
        action: actionFilter || undefined,
        userId: userFilter ? parseInt(userFilter, 10) : undefined,
      });
      if (res.success && res.events) {
        setEvents(res.events);
      }
    } catch (e) {
      /* géré par fetchOnline */
    }
  }, [limit, appFilter, actionFilter, userFilter]);

  const refreshAll = useCallback(async () => {
    await Promise.all([fetchOnline(), fetchHistory()]);
    setLoading(false);
  }, [fetchOnline, fetchHistory]);

  // Chargement initial
  useEffect(() => {
    refreshAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Recharger l'historique quand les filtres changent
  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  // Auto-refresh de la présence en ligne
  useEffect(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (autoRefresh) {
      intervalRef.current = setInterval(() => fetchOnline(true), ONLINE_REFRESH_MS);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [autoRefresh, fetchOnline]);

  // Enrichit la liste des utilisateurs connus (accumulée pour rester stable même filtrée)
  useEffect(() => {
    setKnownUsers((prev) => {
      const m = new Map<number, string>();
      prev.forEach((u) => m.set(u.id, u.name));
      online.forEach((u) => m.set(u.userId, u.displayName || u.username));
      events.forEach((e) => {
        const name = e.displayName || e.username;
        if (name) m.set(e.userId, name);
      });
      return Array.from(m.entries())
        .map(([id, name]) => ({ id, name }))
        .sort((a, b) => a.name.localeCompare(b.name));
    });
  }, [online, events]);

  const isAdminRole = (role?: string) => (role || '').toLowerCase() === 'admin';
  const visibleOnline = online.filter((u) => !hideAdmin || !isAdminRole(u.role));
  const visibleEvents = events.filter((e) => !hideAdmin || !isAdminRole(e.role));

  const appsInHistory = Array.from(new Set(events.map((e) => e.app))).sort();

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto' }}>
      {/* En-tête */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 12,
          marginBottom: 24,
        }}
      >
        <div>
          <h1 style={{ fontSize: '1.6rem', fontWeight: 600, color: '#fff', margin: '0 0 4px 0' }}>
            Activité des utilisateurs
          </h1>
          <p style={{ fontSize: '0.9rem', color: '#94a3b8', margin: 0 }}>
            Qui est en ligne et historique des accès aux applications
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontSize: '0.8rem',
              color: '#94a3b8',
              cursor: 'pointer',
            }}
          >
            <input
              type="checkbox"
              checked={hideAdmin}
              onChange={(e) => setHideAdmin(e.target.checked)}
            />
            Masquer l'admin
          </label>
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontSize: '0.8rem',
              color: '#94a3b8',
              cursor: 'pointer',
            }}
          >
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
            />
            Auto-rafraîchir
          </label>
          <button
            onClick={() => refreshAll()}
            disabled={refreshing}
            style={{
              background: '#23262a',
              border: '1px solid #353a40',
              color: '#cbd5e1',
              borderRadius: 8,
              padding: '8px 14px',
              fontSize: '0.85rem',
              cursor: refreshing ? 'default' : 'pointer',
              opacity: refreshing ? 0.6 : 1,
            }}
          >
            {refreshing ? 'Actualisation…' : 'Actualiser'}
          </button>
        </div>
      </div>

      {error && (
        <div
          style={{
            background: 'rgba(239,68,68,0.1)',
            border: '1px solid rgba(239,68,68,0.3)',
            color: '#f87171',
            borderRadius: 8,
            padding: '10px 14px',
            marginBottom: 16,
            fontSize: '0.85rem',
          }}
        >
          {error}
        </div>
      )}

      {/* Section En ligne */}
      <section style={{ marginBottom: 32 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            marginBottom: 14,
          }}
        >
          <span style={{ position: 'relative', display: 'inline-flex' }}>
            <span
              style={{
                width: 9,
                height: 9,
                borderRadius: '50%',
                background: '#4ade80',
                boxShadow: '0 0 0 3px rgba(74,222,128,0.2)',
              }}
            />
          </span>
          <h2 style={{ fontSize: '1.05rem', fontWeight: 600, color: '#fff', margin: 0 }}>
            En ligne maintenant
          </h2>
          <span
            style={{
              fontSize: '0.75rem',
              color: '#94a3b8',
              background: '#23262a',
              border: '1px solid #353a40',
              borderRadius: 9999,
              padding: '1px 9px',
            }}
          >
            {visibleOnline.length}
          </span>
          {lastRefresh && (
            <span style={{ fontSize: '0.7rem', color: '#8b919a', marginLeft: 'auto' }}>
              MAJ {lastRefresh.toLocaleTimeString('fr-FR')}
            </span>
          )}
        </div>

        {loading ? (
          <div style={{ color: '#64748b', fontSize: '0.9rem', padding: 20 }}>Chargement…</div>
        ) : visibleOnline.length === 0 ? (
          <div
            style={{
              color: '#64748b',
              fontSize: '0.9rem',
              padding: 20,
              background: '#23262a',
              border: '1px solid #353a40',
              borderRadius: 10,
              textAlign: 'center',
            }}
          >
            Aucun utilisateur actif sur les 2 dernières minutes.
          </div>
        ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
              gap: 12,
            }}
          >
            {visibleOnline.map((u) => (
              <div
                key={u.userId}
                onClick={() =>
                  setUserFilter((prev) => (prev === String(u.userId) ? '' : String(u.userId)))
                }
                title="Filtrer l'historique sur cet utilisateur"
                style={{
                  background: '#23262a',
                  border: `1px solid ${userFilter === String(u.userId) ? '#4ade80' : '#353a40'}`,
                  borderRadius: 10,
                  padding: 14,
                  cursor: 'pointer',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <span
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: '#4ade80',
                      flexShrink: 0,
                    }}
                  />
                  <span
                    style={{
                      fontWeight: 600,
                      color: '#fff',
                      fontSize: '0.9rem',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {u.displayName || u.username}
                  </span>
                  <span style={{ marginLeft: 'auto' }}>
                    <RoleBadge role={u.role} />
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <AppBadge app={u.app} />
                </div>
                <div
                  style={{
                    fontSize: '0.72rem',
                    color: '#64748b',
                  }}
                >
                  <span>{relativeTime(u.lastSeen)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Section Historique */}
      <section>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            flexWrap: 'wrap',
            marginBottom: 14,
          }}
        >
          <h2 style={{ fontSize: '1.05rem', fontWeight: 600, color: '#fff', margin: 0 }}>
            Historique récent
          </h2>
          <div style={{ display: 'flex', gap: 8, marginLeft: 'auto', flexWrap: 'wrap' }}>
            <select
              value={userFilter}
              onChange={(e) => setUserFilter(e.target.value)}
              style={selectStyle}
            >
              <option value="">Tous les utilisateurs</option>
              {knownUsers.map((u) => (
                <option key={u.id} value={String(u.id)}>
                  {u.name}
                </option>
              ))}
            </select>
            <select
              value={appFilter}
              onChange={(e) => setAppFilter(e.target.value)}
              style={selectStyle}
            >
              <option value="">Toutes les apps</option>
              {appsInHistory.map((a) => (
                <option key={a} value={a}>
                  {appLabel(a)}
                </option>
              ))}
            </select>
            <select
              value={actionFilter}
              onChange={(e) => setActionFilter(e.target.value)}
              style={selectStyle}
            >
              <option value="">Toutes les actions</option>
              <option value="login">Connexion</option>
              <option value="enter">Ouverture</option>
              <option value="logout">Déconnexion</option>
            </select>
            <select
              value={limit}
              onChange={(e) => setLimit(parseInt(e.target.value, 10))}
              style={selectStyle}
            >
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={200}>200</option>
              <option value={500}>500</option>
            </select>
          </div>
        </div>

        <div
          style={{
            background: '#23262a',
            border: '1px solid #353a40',
            borderRadius: 10,
            overflow: 'hidden',
          }}
        >
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
              <thead>
                <tr style={{ background: '#181b1f' }}>
                  {['Date', 'Utilisateur', 'Rôle', 'Application', 'Action'].map((h) => (
                    <th
                      key={h}
                      style={{
                        textAlign: 'left',
                        padding: '10px 14px',
                        color: '#64748b',
                        fontWeight: 600,
                        fontSize: '0.7rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibleEvents.length === 0 ? (
                  <tr>
                    <td
                      colSpan={5}
                      style={{ padding: 24, textAlign: 'center', color: '#64748b' }}
                    >
                      Aucun événement.
                    </td>
                  </tr>
                ) : (
                  visibleEvents.map((ev) => {
                    const action = ACTION_LABELS[ev.action] || {
                      label: ev.action,
                      color: '#cbd5e1',
                      bg: '#181b1f',
                    };
                    return (
                      <tr key={ev.id} style={{ borderTop: '1px solid #353a40' }}>
                        <td style={{ padding: '9px 14px', color: '#94a3b8', whiteSpace: 'nowrap' }}>
                          {formatDateTime(ev.createdAt)}
                        </td>
                        <td style={{ padding: '9px 14px', color: '#fff', fontWeight: 500 }}>
                          {ev.displayName || ev.username}
                        </td>
                        <td style={{ padding: '9px 14px' }}>
                          <RoleBadge role={ev.role} />
                        </td>
                        <td style={{ padding: '9px 14px' }}>
                          <AppBadge app={ev.app} />
                        </td>
                        <td style={{ padding: '9px 14px' }}>
                          <span
                            style={{
                              fontSize: '0.72rem',
                              fontWeight: 600,
                              color: action.color,
                              background: action.bg,
                              borderRadius: 6,
                              padding: '2px 8px',
                            }}
                          >
                            {action.label}
                          </span>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>
  );
};

const selectStyle: React.CSSProperties = {
  background: '#181b1f',
  border: '1px solid #353a40',
  color: '#cbd5e1',
  borderRadius: 8,
  padding: '6px 10px',
  fontSize: '0.8rem',
  cursor: 'pointer',
};

export default ActivityMonitor;
